/**
 * Draws the Invoice 4404 PDF template in the provided jsPDF instance.
 * Based on design "Invoice 4404-3.pdf".
 *
 * @param {import("jspdf").jsPDF} doc
 * @param {object} data
 */
export function drawInvoice4404Template(doc, data = {}) {
  const PAGE_W = 612;
  const PAGE_H = 792;
  const HEADER_TOP = 24;
  const LOGO_WIDTH = 160;
  const LOGO_HEIGHT = 40;

  // Left content margin used in the PDF
  const X_LEFT = 44.25;

  // Common right-alignment anchor in the PDF (end of money values)
  const X_RIGHT = 568; // ~567.6 in PDF; rounding for jsPDF is fine

  const CARD_PADDING_X = 14;
  const CARD_LEFT = X_LEFT - CARD_PADDING_X;
  const CARD_WIDTH = PAGE_W - CARD_LEFT * 2;
  const HEADER_DIVIDER_Y = 164;

  // Column anchors (from the PDF header row)
  const COL_ITEM_X = 44.25;
  const COL_QTY_X = 371.25;
  const COL_RATE_RIGHT_X = 492.32; // right edge of "Rate" header
  const COL_AMT_RIGHT_X = 567.57; // right edge of "Amount" header

  // Typography
  const FONT = "helvetica";
  const BRAND_PRIMARY = { r: 195, g: 62, b: 58 }; // #c33e3a
  const TABLE_HEADER_BG = { r: 51, g: 51, b: 51 }; // charcoal
  const TEXT_DEFAULT = { r: 44, g: 37, b: 38 };
  const MUTED_TEXT = { r: 133, g: 111, b: 113 };

  doc.setFont(FONT, "normal");
  doc.setTextColor(TEXT_DEFAULT.r, TEXT_DEFAULT.g, TEXT_DEFAULT.b);

  // Plain white background
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");
  doc.setDrawColor(237, 231, 229);
  doc.setLineWidth(0.5);
  doc.line(CARD_LEFT, HEADER_DIVIDER_Y, X_RIGHT + 10, HEADER_DIVIDER_Y);

  // Brand logo at top-left if provided
  if (data.logoDataUrl) {
    try {
      doc.addImage(
        data.logoDataUrl,
        "PNG",
        X_LEFT,
        HEADER_TOP,
        LOGO_WIDTH,
        LOGO_HEIGHT,
        undefined,
        "FAST"
      );
    } catch (err) {
      // ignore logo errors to avoid breaking PDF
    }
  }

  // Restore text defaults for content
  doc.setTextColor(TEXT_DEFAULT.r, TEXT_DEFAULT.g, TEXT_DEFAULT.b);

  // Helper: safe text
  const T = (v) => (v == null ? "" : String(v));

  const docTitle = T(data.docTitle || "INVOICE");
  const docNumber = T(data.docNumber || data.invoiceNumber || "4404");
  const barcodeValue = T(data.barcodeValue || data.barcode || "");
  const barcodeDataUrl = data.barcodeDataUrl;

  // Helper: wrapped text in a max width
  const wrap = (text, maxWidth) => doc.splitTextToSize(T(text), maxWidth);

  // -------------------------
  // Top-right header
  // -------------------------
  // INVOICE / RECEIPT
  doc.setFont(FONT, "bold");
  doc.setFontSize(24);
  doc.setTextColor(BRAND_PRIMARY.r, BRAND_PRIMARY.g, BRAND_PRIMARY.b);
  doc.text(docTitle, X_RIGHT, HEADER_TOP, { align: "right", baseline: "top" });

  // # <number>
  doc.setFontSize(16);
  doc.text(`# ${docNumber}`, X_RIGHT, HEADER_TOP + 34, {
    align: "right",
    baseline: "top",
  });

  // Barcode (optional)
  if (barcodeValue || barcodeDataUrl) {
    const BARCODE_WIDTH = 170;
    const BARCODE_HEIGHT = 48;
    const BARCODE_TOP = HEADER_TOP + 64;
    const BARCODE_X = X_RIGHT - BARCODE_WIDTH;
    if (barcodeDataUrl) {
      try {
        doc.addImage(
          barcodeDataUrl,
          "PNG",
          BARCODE_X,
          BARCODE_TOP,
          BARCODE_WIDTH,
          BARCODE_HEIGHT,
          undefined,
          "FAST"
        );
      } catch (_err) {
        // ignore barcode rendering issues
      }
    }
    doc.setFont(FONT, "normal");
    doc.setFontSize(8);
    doc.setTextColor(MUTED_TEXT.r, MUTED_TEXT.g, MUTED_TEXT.b);
    doc.text(barcodeValue ? `Barcode: ${barcodeValue}` : "Barcode", X_RIGHT, BARCODE_TOP + BARCODE_HEIGHT + 10, {
      align: "right",
      baseline: "top",
    });
  }

  // -------------------------
  // Company block (address beneath logo)
  // -------------------------
  doc.setFont(FONT, "normal");
  doc.setFontSize(10);
  doc.setTextColor(MUTED_TEXT.r, MUTED_TEXT.g, MUTED_TEXT.b);

  const ADDRESS_TOP = HEADER_TOP + LOGO_HEIGHT + 6;
  const companyLines = [
    T(data.companyName || "High Window Medical Services"),
    T(
      data.companyAddrLine1 ||
        "Hawali, Block 8, Suraqah bin Malik Street, Floor 2,"
    ),
    T(data.companyAddrLine2 || "Office 22."),
    T(data.companyAddrLine3 || "Kuwait City, Kuwait."),
    T(data.companyEmail || "Info@highwindow-kwt.com"),
    T(data.companyPhone || "+965-90044801"),
  ].filter((line) => line && line.trim());

  companyLines.forEach((line, idx) => {
    doc.text(line, X_LEFT, ADDRESS_TOP + idx * 12, { baseline: "top" });
  });

  // -------------------------
  // Measurements + layout
  // -------------------------
  const billToLinesRaw = Array.isArray(data.billTo)
    ? data.billTo
    : data.billTo
    ? [T(data.billTo)]
    : [];
  const billToLines = billToLinesRaw
    .map((line) => T(line))
    .filter((line) => line && line.trim());
  const BILL_TO_LINE_HEIGHT = 14;
  const CONTENT_CARD_TOP = HEADER_DIVIDER_Y + 12;
  const BILL_TO_LABEL_Y = CONTENT_CARD_TOP + 16;
  const BILL_TO_TEXT_Y = BILL_TO_LABEL_Y + 14;
  const billToLineCount = Math.max(1, billToLines.length);

  const rightInfoRows = [
    { label: "Date:", value: T(data.date || "—") },
    { label: "Due date:", value: T(data.dueDate || "—") },
    { label: "Status:", value: T(data.status || "—") },
    { label: "Balance Due:", value: T(data.balanceDue || "—") },
  ];

  const BILL_TO_BLOCK_HEIGHT = billToLineCount * BILL_TO_LINE_HEIGHT;
  const RIGHT_INFO_LINE_HEIGHT = 14;
  const rightInfoLineCount = Math.max(1, rightInfoRows.length);
  const rightInfoStartY = BILL_TO_LABEL_Y;
  const rightInfoBottom =
    rightInfoStartY + rightInfoLineCount * RIGHT_INFO_LINE_HEIGHT;

  const billToBottom = BILL_TO_TEXT_Y + BILL_TO_BLOCK_HEIGHT;

  const MIN_TABLE_HEADER_Y = CONTENT_CARD_TOP + 64;
  const Y_TABLE_HEADER = Math.max(
    MIN_TABLE_HEADER_Y,
    billToBottom + 14,
    rightInfoBottom + 14
  );
  const TABLE_HEADER_HEIGHT = 28;
  const HEADER_TEXT_Y = Y_TABLE_HEADER + 8;

  const items =
    Array.isArray(data.items) && data.items.length ? data.items : [];

  const ITEM_MAX_W = COL_QTY_X - COL_ITEM_X - 14; // small gutter
  const ITEM_LINE_HEIGHT = 12;
  let currentItemY = Y_TABLE_HEADER + TABLE_HEADER_HEIGHT + 8;
  const renderedItems = [];

  items.forEach((it) => {
    const nameLines = wrap(it.name, ITEM_MAX_W);
    const nameHeight = Math.max(
      nameLines.length * ITEM_LINE_HEIGHT,
      ITEM_LINE_HEIGHT
    );
    const dividerY = currentItemY + nameHeight + ITEM_LINE_HEIGHT + 6;
    renderedItems.push({
      y: currentItemY,
      nameLines,
      dividerY,
      qty: T(it.qty),
      rate: T(it.rate),
      amount: T(it.amount),
    });
    currentItemY = dividerY + 6;
  });

  const itemsBottomY = renderedItems.length
    ? renderedItems[renderedItems.length - 1].dividerY
    : currentItemY;
  const totalsStartY = Math.max(itemsBottomY + 10, Y_TABLE_HEADER + 56);
  const TOTAL_LINE_GAP = 18;
  const Y_SUBTOTAL = totalsStartY;
  const totalsRows = [
    {
      label: "Subtotal:",
      value: T(data.subtotal || "KWD 200.000"),
      bold: false,
    },
  ];
  if (data.additionLabel) {
    totalsRows.push({
      label: `${T(data.additionLabel)}:`,
      value: T(data.additionAmount || data.addition || "KWD 0.000"),
      bold: false,
    });
  }
  const includeTax =
    data.showTax !== false &&
    data.tax !== undefined &&
    data.tax !== null &&
    data.tax !== "";
  if (includeTax) {
    totalsRows.push({
      label: T(data.taxLabel || "Tax:"),
      value: T(data.tax || "KWD 0.000"),
      bold: false,
    });
  }
  totalsRows.push({
    label: "Total:",
    value: T(data.total || "KWD 200.000"),
    bold: true,
  });

  const totalsHeight = totalsRows.length
    ? (totalsRows.length - 1) * TOTAL_LINE_GAP
    : 0;
  const TOTALS_BOTTOM = Y_SUBTOTAL + totalsHeight + 12;

  const CONTENT_CARD_BOTTOM = Math.max(TOTALS_BOTTOM + 14, itemsBottomY + 20);
  const CONTENT_CARD_HEIGHT = CONTENT_CARD_BOTTOM - CONTENT_CARD_TOP;

  const notes =
    Array.isArray(data.notes) && data.notes.length ? data.notes : [];
  const NOTES_TOP = CONTENT_CARD_BOTTOM + 22;
  const NOTES_MAX_W = 420; // safe width before right totals area
  const notesLines = notes
    .flatMap((n) => wrap(n, NOTES_MAX_W))
    .filter((line) => String(line).trim());
  const NOTES_LINE_HEIGHT = 12;
  const hasNotes = notesLines.length > 0;
  const notesHeight = hasNotes
    ? Math.max(NOTES_LINE_HEIGHT, notesLines.length * NOTES_LINE_HEIGHT)
    : 0;
  const NOTES_CARD_MIN_HEIGHT = 140;
  const NOTES_CARD_HEIGHT = hasNotes
    ? Math.max(NOTES_CARD_MIN_HEIGHT, notesHeight + 32)
    : 0;

  // Soft card backgrounds sized to content
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(255, 224, 222);
  doc.rect(CARD_LEFT, CONTENT_CARD_TOP, CARD_WIDTH, CONTENT_CARD_HEIGHT, "F");
  doc.rect(CARD_LEFT, CONTENT_CARD_TOP, CARD_WIDTH, CONTENT_CARD_HEIGHT);
  if (hasNotes) {
    doc.rect(CARD_LEFT, NOTES_TOP, CARD_WIDTH, NOTES_CARD_HEIGHT, "F");
    doc.rect(CARD_LEFT, NOTES_TOP, CARD_WIDTH, NOTES_CARD_HEIGHT);
  }

  // -------------------------
  // Bill To (left)
  // -------------------------
  doc.setFont(FONT, "bold");
  doc.setFontSize(10);
  doc.setTextColor(BRAND_PRIMARY.r, BRAND_PRIMARY.g, BRAND_PRIMARY.b);
  doc.text("Bill To:", X_LEFT, BILL_TO_LABEL_Y, { baseline: "top" });

  doc.setFont(FONT, "normal");
  doc.setFontSize(11);
  doc.setTextColor(TEXT_DEFAULT.r, TEXT_DEFAULT.g, TEXT_DEFAULT.b);
  doc.text(billToLines.length ? billToLines : [""], X_LEFT, BILL_TO_TEXT_Y, {
    baseline: "top",
  });

  // Due/date/currency block on the right of Bill To
  const RIGHT_INFO_LABEL_X = X_RIGHT - 150;
  const RIGHT_INFO_VALUE_X = X_RIGHT;
  doc.setFontSize(10);
  rightInfoRows.forEach((row, idx) => {
    const y = rightInfoStartY + idx * RIGHT_INFO_LINE_HEIGHT;
    doc.setFont(FONT, "bold");
    doc.setTextColor(BRAND_PRIMARY.r, BRAND_PRIMARY.g, BRAND_PRIMARY.b);
    doc.text(row.label, RIGHT_INFO_LABEL_X, y, { baseline: "top" });
    doc.setFont(FONT, "bold");
    doc.setTextColor(TEXT_DEFAULT.r, TEXT_DEFAULT.g, TEXT_DEFAULT.b);
    doc.text(row.value, RIGHT_INFO_VALUE_X, y, { align: "right", baseline: "top" });
  });

  // -------------------------
  // Table Header
  // -------------------------
  const TABLE_BG_X = CARD_LEFT;
  const TABLE_BG_WIDTH = CARD_WIDTH;
  doc.setFont(FONT, "bold");
  doc.setFontSize(10);
  doc.setFillColor(TABLE_HEADER_BG.r, TABLE_HEADER_BG.g, TABLE_HEADER_BG.b);
  doc.rect(
    TABLE_BG_X,
    Y_TABLE_HEADER,
    TABLE_BG_WIDTH,
    TABLE_HEADER_HEIGHT,
    "F"
  );
  doc.setTextColor(255, 255, 255);

  doc.text("Item", COL_ITEM_X, HEADER_TEXT_Y, { baseline: "top" });
  doc.text("Quantity", COL_QTY_X, HEADER_TEXT_Y, { baseline: "top" });
  doc.text("Rate", COL_RATE_RIGHT_X, HEADER_TEXT_Y, {
    align: "right",
    baseline: "top",
  });
  doc.text("Amount", COL_AMT_RIGHT_X, HEADER_TEXT_Y, {
    align: "right",
    baseline: "top",
  });

  // -------------------------
  // Line Items (start positions match PDF rows)
  // -------------------------
  doc.setFont(FONT, "normal");
  doc.setFontSize(10);
  doc.setTextColor(TEXT_DEFAULT.r, TEXT_DEFAULT.g, TEXT_DEFAULT.b);

  // subtle lines for rows
  doc.setDrawColor(255, 223, 217);

  renderedItems.forEach((row) => {
    doc.text(row.nameLines, COL_ITEM_X, row.y, { baseline: "top" });
    doc.text(row.qty, COL_QTY_X, row.y, { baseline: "top" });
    doc.text(row.rate, COL_RATE_RIGHT_X, row.y, {
      align: "right",
      baseline: "top",
    });
    doc.text(row.amount, COL_AMT_RIGHT_X, row.y, {
      align: "right",
      baseline: "top",
    });

    doc.line(TABLE_BG_X, row.dividerY, X_RIGHT, row.dividerY);
  });

  // -------------------------
  // Totals (right block)
  // -------------------------
  const X_TOTAL_LABEL = 423.75;

  let currentTotalY = Y_SUBTOTAL;
  totalsRows.forEach((row) => {
    const isTotal = row.bold;
    doc.setFont(FONT, isTotal ? "bold" : "normal");
    doc.setFontSize(10);
    if (isTotal) {
      doc.setTextColor(BRAND_PRIMARY.r, BRAND_PRIMARY.g, BRAND_PRIMARY.b);
    } else {
      doc.setTextColor(MUTED_TEXT.r, MUTED_TEXT.g, MUTED_TEXT.b);
    }
    doc.text(row.label, X_TOTAL_LABEL, currentTotalY, { baseline: "top" });
    doc.text(row.value, X_RIGHT, currentTotalY, {
      align: "right",
      baseline: "top",
    });
    currentTotalY += TOTAL_LINE_GAP;
  });

  // Footer accent / watermark
  // Draw before notes text so notes always render above it.
  const supportsGraphicsState =
    typeof doc.saveGraphicsState === "function" &&
    typeof doc.restoreGraphicsState === "function";
  if (supportsGraphicsState) {
    doc.saveGraphicsState();
  }
  doc.setFont(FONT, "bold");
  doc.setFontSize(32);
  if (typeof doc.setGState === "function") {
    try {
      const watermarkOpacity = 0.03;
      const watermarkState =
        typeof doc.GState === "function"
          ? new doc.GState({
              opacity: watermarkOpacity,
              "stroke-opacity": watermarkOpacity,
            })
          : {
              opacity: watermarkOpacity,
              "stroke-opacity": watermarkOpacity,
            };
      doc.setGState(watermarkState);
      doc.setTextColor(BRAND_PRIMARY.r, BRAND_PRIMARY.g, BRAND_PRIMARY.b);
    } catch (_err) {
      // Fallback for environments that reject custom graphics state.
      doc.setTextColor(236, 229, 228);
    }
  } else {
    doc.setTextColor(236, 229, 228);
  }
  doc.text("High Window Medical Services", PAGE_W / 2, PAGE_H - 24, {
    align: "center",
  });
  if (supportsGraphicsState) {
    doc.restoreGraphicsState();
  }

  // -------------------------
  // Notes (bottom-left)
  // -------------------------
  if (hasNotes) {
    doc.setFont(FONT, "bold");
    doc.setFontSize(10);
    doc.setTextColor(BRAND_PRIMARY.r, BRAND_PRIMARY.g, BRAND_PRIMARY.b);
    doc.text("Notes:", X_LEFT, NOTES_TOP + 14, { baseline: "top" });

    doc.setFont(FONT, "normal");
    doc.setFontSize(10);
    doc.setTextColor(TEXT_DEFAULT.r, TEXT_DEFAULT.g, TEXT_DEFAULT.b);
    doc.text(notesLines, X_LEFT, NOTES_TOP + 30, { baseline: "top" });
  }

  // Pagination footer
  const pageCount = doc.getNumberOfPages ? doc.getNumberOfPages() : 1;
  if (pageCount > 0 && doc.setPage) {
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFont(FONT, "normal");
      doc.setFontSize(8);
      doc.setTextColor(MUTED_TEXT.r, MUTED_TEXT.g, MUTED_TEXT.b);
      doc.text(`Page ${i} of ${pageCount}`, PAGE_W / 2, PAGE_H - 6, {
        align: "center",
        baseline: "bottom",
      });
    }
  }

  return doc;
}
